import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Select, Col, Button, Alert } from 'antd';
import { useQuery, useMutation } from '@tanstack/react-query';
import useApi from 'vl-common/src/hooks/useApi';
import { AlertMessage } from 'vl-common/src/components/AlertMessage/AlertMessage';
import { Task } from 'vl-common/src/types';

interface CancelTaskModalProps {
  show: boolean;
  selectedTask: Task | undefined;
  onClose: () => void;
  onCompleted: () => void;
}

const CancelTaskModalContent = ({ selectedTask, onClose, onCompleted }: Omit<CancelTaskModalProps, 'show'>) => {
  const { t } = useTranslation('common');
  const { Api } = useApi();
  const [selectedItem, setSelectedItem] = useState<string>();
  const [showAlertMessage, setShowAlertMessage] = useState({
    visibility: false,
    msg: ''
  });
  const isSelected = selectedItem !== '';

  const { data: cancelReasons, isLoading: isCancelReasonsLoading } = useQuery({
    queryKey: ['cancelReasons'],
    queryFn: async () => {
      const response = await Api.meta.getTaskMeta();
      return response.data?.meta?.[0]?.cancel_reason;
    }
  });

  const { mutate: cancelTask, isPending: isCancelTaskLoading } = useMutation({
    mutationKey: ['cancelTask', selectedTask?.task_id],
    mutationFn: async () =>
      await Api.task.cancel({
        task_id: selectedTask?.task_id,
        cancel_reason: selectedItem
      }),
    onError: () => displayAlertMessage('Bad Request, Try again.'),
    onSuccess: onCompleted
  });

  const displayAlertMessage = (msg = '') => {
    setShowAlertMessage({ visibility: true, msg });
    const t = setTimeout(() => {
      setShowAlertMessage({ visibility: false, msg });
      clearTimeout(t);
    }, 7000);
  };

  useEffect(() => {
    setSelectedItem('');
  }, []);

  return (
    <Modal
      title={`Cancel Task: ${selectedTask?.task_id}`}
      data-testid="cancel-task-modal"
      open
      onCancel={onClose}
      footer={[
        <Button key="cancel" data-testid="cancel-task-cancel" onClick={onClose}>
          {t('Cancel')}
        </Button>,
        <Button
          onClick={() => cancelTask()}
          key="submit"
          type="primary"
          data-testid="cancel-task-submit"
          loading={isCancelTaskLoading}
          disabled={!isSelected}
        >
          {t('Cancel Task')}
        </Button>
      ]}
    >
      <AlertMessage message={showAlertMessage.msg} visibility={showAlertMessage.visibility} />
      <Col
        style={{
          width: '90%',
          display: 'flex',
          alignItems: 'start',
          flexDirection: 'column',
          margin: '0 auto',
          gap: '8px'
        }}
      >
        {selectedTask?.task_type_code === 'ELIGIBILITY' ? (
          <Alert
            type="warning"
            showIcon
            message={t('Eligibility task warning')}
            description={t('Cancelling this eligibility task will mark the eligibility check as ineligible')}
          />
        ) : null}
        <span style={{ fontSize: '14px' }}>{t('Please select a reason why you are cancelling the task')}</span>
        <Select
          value={selectedItem}
          placeholder={t('Select from dropdown')}
          onChange={(value) => setSelectedItem(value)}
          data-testid="cancel-task-reason-select"
          style={{ width: '100%' }}
          loading={isCancelReasonsLoading}
        >
          {cancelReasons?.map((data: { reason_code: string; description: string }) => (
            <Select.Option key={data.reason_code} value={data.reason_code}>
              {data.description}
            </Select.Option>
          ))}
        </Select>
      </Col>
    </Modal>
  );
};

const CancelTaskModal = ({ show, ...rest }: CancelTaskModalProps) => show && <CancelTaskModalContent {...rest} />;

export default CancelTaskModal;
